import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** Malahide Castle (m): where its front stands, from the camera up in the air over the park. */
// (owner: from more in front and a little farther off)
const CASTLE = { x: -118, z: 300 };
/** (owner: the castle a little bigger, then 10% more) drawn at 1.43 times about the foot of its front. */
const CASTLE_KS = 1.43;
/** Where the woods start behind the lawns (m), one line across the park. */
// (owner: the woods farther forward, no open ground before the castle, the castle where it is)
const WOODS_EDGE = 150;
/** The near side of the castle's garden (m): the woods close in again before it. */
const GARDEN_Z = 262;

/** A raindrop, in page pixels. */
interface Drop {
  x: number;
  y: number;
  len: number;
  speed: number;
  rank: number;
  alpha: number;
}

/**
 * Dublin's hero board (2026-10-18), after the owner's photograph of Malahide Castle from the air: in true
 * perspective (a camera 30 m up over the park, everything in metres), a soft Irish sky with drifting clouds
 * over the sea, the estuary and the island on the horizon; the woods round the park in many greens with the
 * orange of autumn in them; the castle of grey stone with its two round crenellated towers and slate roofs;
 * the mown lawns in stripes before the woods' gentle arc, the castle's square garden in a clearing. A light Irish rain falls,
 * heavier on the charge (owner); the page's wallpaper is the raindrops. Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-dublin',
  template: '<canvas #canvas class="hdub-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hdub-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroDublinComponent extends HeroMotifBase {

  /**
   * (owner: Dublin stuttered) the sky and its clouds in one tile two screens wide, only as tall as the sky:
   * the clouds drift by sliding it, one draw a frame instead of three full ones
   */
  private skyTile: HTMLCanvasElement | null = null;
  private skyH = 0;
  /** The land: the sea, the woods, the lawns, the drive, the castle. */
  private back: HTMLCanvasElement | null = null;
  /** The castle, on its own so the woods round it can sway. */
  private castleLayer: HTMLCanvasElement | null = null;
  /** The woods in front of the castle. */
  private frontWoods: HTMLCanvasElement | null = null;
  private swayTop = 0;
  private swayBottom = 0;
  /** The rows each layer holds (page pixels), so only those are drawn, and the castle's own box. */
  private backRows: [ number, number, number ] = [ 0, 0, 0 ];
  private frontRows: [ number, number ] = [ 0, 0 ];
  private castleRect: [ number, number, number, number ] = [ 0, 0, 0, 0 ];
  /**
   * What the layers were baked for (size and density): a re-layout at the same size keeps them, and while the
   * stage is being resized the old ones are drawn scaled until the size has held for a moment.
   */
  private bakedFor = '';
  private bW = 1;
  private bH = 1;
  private pendingFor = '';
  private pendingAt = 0;
  private drops: Drop[] = [];
  private scaleK = 1;
  // (owner: from in front) high over the park as the drone photograph, facing the castle's front
  private eye = 45;
  private F = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.bakedFor = '';
  }

  /** Project a point: x across (metres), y up, z away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  protected build(): void {
    const W = this.width, H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.F = 1.35 * H;
    // (owner: the whole picture lowered in steps: 20, 10, 20, 20 and 30 px)
    this.hz = 0.3 * H + 100;
    this.vx = W / 2;
    const r = this.seededLocal( 41 ), k = this.scaleK;
    // (owner: the charge brings rain, not leaves) a pool of drops, each falling while the charge reaches it
    this.drops = [];
    const n = Math.max( 300, Math.round( W * H / 1800 ) );
    for ( let i = 0; i < n; i++ ) {
      this.drops.push( { x: r() * W, y: r() * H, len: ( 12 + r() * 18 ) * k, speed: ( 650 + r() * 450 ) * k, rank: r(), alpha: 0.18 + r() * 0.22 } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width, H = this.height;
    if ( this.frozen ) {
      // (owner) the page's wallpaper is the raindrops
      this.drawWallRain( ctx );
      return;
    }
    const scale = ctx.getTransform().a, key = W + 'x' + H + '@' + scale, now = performance.now();
    if ( this.bakedFor !== key ) {
      if ( this.pendingFor !== key ) { this.pendingFor = key; this.pendingAt = now; }
      if ( !this.back || !this.bakedFor || this.still || now - this.pendingAt > 300 ) {
        this.bake( scale );
        this.bakedFor = key;
        this.bW = W;
        this.bH = H;
      }
    }
    if ( !this.back || !this.skyTile ) { return; }
    const kx = W / this.bW, ky = H / this.bH;
    // the clouds drift slowly to the right, whole pixels so the tile's seam never shows
    const off = Math.round( ( now / 1000 * 4 * this.scaleK ) % this.bW ), sc = this.skyTile.height / Math.max( 1, this.skyH );
    ctx.drawImage( this.skyTile, ( this.bW - off ) * sc, 0, this.bW * sc, this.skyH * sc, 0, 0, W, this.skyH * ky );
    // the land: its rows of woods swaying, the lawns below them still
    const [ bTop, bSway, bBot ] = this.backRows;
    this.drawSwaying( ctx, this.back, bTop, bSway );
    this.drawRows( ctx, this.back, bSway, bBot );
    if ( this.castleLayer ) {
      const [ cx, cy, cw, ch ] = this.castleRect, cs = this.castleLayer.width / this.bW;
      ctx.drawImage( this.castleLayer, cx * cs, cy * cs, cw * cs, ch * cs, cx * kx, cy * ky, cw * kx, ch * ky );
    }
    if ( this.frontWoods ) {
      const [ fTop, fBot ] = this.frontRows;
      this.drawSwaying( ctx, this.frontWoods, fTop, Math.min( fBot, this.swayBottom ) );
      this.drawRows( ctx, this.frontWoods, this.swayBottom, fBot );
    }
    this.drawRain( ctx, dt );
  }

  /**
   * (owner: the trees swaying a little in the wind) the rows y0 to y1 of a layer drawn in level strips, each
   * nudged sideways by a slow wave running through the woods, more on the charge, easing off toward the
   * woods' near edge (swayBottom); the strips keep their places on the 10 px grid from the top
   */
  private drawSwaying( ctx: CanvasRenderingContext2D, layer: HTMLCanvasElement, y0: number, y1: number ): void {
    const W = this.width, sc = layer.height / this.bH, ky = this.height / this.bH, t = this.time, e = this.energy, edge = this.swayBottom;
    const amp = ( 0.9 + 0.9 * e ) * this.scaleK, step = 10;
    for ( let y = Math.max( 0, Math.floor( y0 / step ) * step ); y < y1; y += step ) {
      const h = Math.min( step, y1 - y ), off = Math.sin( t * W0 * 4 + y * 0.045 ) * amp * Math.min( 1, ( edge - y ) / 40 + 0.2 );
      // (owner: white showed at the far left and right) each strip 3 px wider at both ends, so its shift never
      // uncovers the frame's edge
      ctx.drawImage( layer, 0, y * sc, layer.width, h * sc, off - 3, y * ky, W + 6, h * ky );
    }
  }

  /** The rows y0 to y1 of a layer drawn whole, still. */
  private drawRows( ctx: CanvasRenderingContext2D, layer: HTMLCanvasElement, y0: number, y1: number ): void {
    const W = this.width, sc = layer.height / this.bH, ky = this.height / this.bH;
    if ( y1 - y0 < 0.5 ) { return; }
    ctx.drawImage( layer, 0, y0 * sc, layer.width, ( y1 - y0 ) * sc, 0, y0 * ky, W, ( y1 - y0 ) * ky );
  }

  /** The rain's colour: a soft grey blue that shows on the pale sky and the green alike. */
  private rainTone(): RGB {
    return this.mix( [ 110, 130, 150 ], this.five()[ 2 ], 0.12 );
  }

  /**
   * (owner) the rain, as Roraima's: a light drizzle at rest, heavier on the charge, each drop a thin streak
   * slanting a little with the wind off the sea
   */
  private drawRain( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width, H = this.height, e = this.energy, slant = 0.16;
    const share = 0.08 + 0.92 * e;
    ctx.save();
    ctx.strokeStyle = this.rgba( this.rainTone(), 1 );
    ctx.lineWidth = Math.max( 0.7, 1.0 * this.scaleK );
    ctx.lineCap = 'round';
    // (owner: Dublin stuttered) the drops stroked in six strengths, one path each, not one stroke a drop
    const bands = 6, paths: Path2D[] = [];
    for ( let b = 0; b < bands; b++ ) { paths.push( new Path2D() ); }
    for ( const d of this.drops ) {
      const v = d.speed * ( 1 + 0.4 * e );
      d.y += v * dt;
      d.x += v * slant * dt;
      if ( d.y - d.len > H ) { d.y = -d.len - Math.random() * H * 0.2; d.x = Math.random() * ( W + H * slant ) - H * slant; }
      if ( d.x > W + 20 ) { d.x -= W + H * slant; }
      if ( d.rank > share ) { continue; }
      const p = paths[ Math.min( bands - 1, Math.max( 0, Math.floor( ( d.alpha - 0.18 ) / 0.22 * bands ) ) ) ];
      p.moveTo( d.x, d.y );
      p.lineTo( d.x - d.len * slant, d.y - d.len );
    }
    for ( let b = 0; b < bands; b++ ) {
      ctx.globalAlpha = ( 0.18 + ( b + 0.5 ) * 0.22 / bands ) * ( 0.75 + 0.4 * e );
      ctx.stroke( paths[ b ] );
    }
    ctx.restore();
  }

  /** (owner) the wallpaper: the raindrops, still, all down the page. */
  private drawWallRain( ctx: CanvasRenderingContext2D ): void {
    const W = this.width, H = this.height, r = this.seededLocal( 77 ), k = this.scaleK, slant = 0.16;
    ctx.save();
    ctx.strokeStyle = this.rgba( this.rainTone(), 1 );
    ctx.lineWidth = Math.max( 0.8, 1.1 * k );
    ctx.lineCap = 'round';
    const n = Math.round( W * H / 9000 );
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, len = ( 14 + r() * 22 ) * k;
      ctx.globalAlpha = 0.14 + r() * 0.2;
      ctx.beginPath(); ctx.moveTo( x, y ); ctx.lineTo( x - len * slant, y - len ); ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * The still parts, painted once, far to near: the sky and its clouds; the sea with the island and the far
   * shore; the woods in rows from the horizon down; the lawns and the drive; the castle among its trees; the
   * near trees at the frame's sides.
   */
  private bake( scale: number ): void {
    const W = this.width, H = this.height, F = this.F, hz = this.hz;
    const [ mint, green, shamrock, orange, gold ] = this.five();
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    let g: CanvasRenderingContext2D;
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    const rr = ( seed: number ) => this.seededLocal( seed );
    // the Irish air: far things paler, a soft grey green blue
    const air: RGB = this.mix( [ 214, 226, 228 ], mint, 0.3 );
    const inAir = ( col: RGB, z: number, k = 0.9 ) => this.mix( col, air, ( 1 - Math.exp( -z / 1400 ) ) * k );
    // the light: a bright soft morning from the upper left
    const SUN: [ number, number, number ] = ( () => { const v = [ -0.6, 0.6, -0.5 ], n = Math.hypot( ...v ); return [ v[ 0 ] / n, v[ 1 ] / n, v[ 2 ] / n ] as [ number, number, number ]; } )();
    const sunK = ( nx: number, nz: number ) => Math.max( 0, nx * SUN[ 0 ] + nz * SUN[ 2 ] ) / Math.hypot( SUN[ 0 ], SUN[ 2 ] );

    // ---- the sky ----
    const sk = canvas();
    g = sk.g;
    const sg = g.createLinearGradient( 0, 0, 0, hz );
    sg.addColorStop( 0, this.rgba( [ 176, 202, 220 ], 1 ) );
    sg.addColorStop( 0.6, this.rgba( [ 214, 228, 234 ], 1 ) );
    sg.addColorStop( 1, this.rgba( this.mix( [ 236, 242, 238 ], mint, 0.2 ), 1 ) );
    g.fillStyle = sg;
    g.fillRect( 0, 0, W, hz + 2 );
    // ---- the clouds: soft grey white banks over the sea, their sunlit tops over grey bases ----
    const cl = canvas();
    g = cl.g;
    {
      const cr = rr( 29 ), white: RGB = [ 255, 255, 255 ], grey: RGB = [ 178, 190, 200 ];
      const banks: [ number, number, number ][] = [];
      for ( let bx = cr() * 0.3 * H; bx < W + H * 0.3; bx += ( 0.5 + cr() * 0.4 ) * H ) { banks.push( [ bx, 0.08 + cr() * 0.2, ( 0.32 + cr() * 0.25 ) * H ] ); }
      for ( const [ bx, v, bw ] of banks ) {
        const by = H * v;
        const puffs: [ number, number, number ][] = [];
        for ( let i = 0; i < 32; i++ ) {
          const a = cr() * 2 - 1, heap = Math.sqrt( 1 - a * a );
          puffs.push( [ bx + a * bw * 0.5, by - cr() * heap * H * 0.06, ( 0.4 + cr() * 0.5 ) * H * 0.045 * ( 0.6 + heap ) ] );
        }
        puffs.sort( ( p2, q2 ) => q2[ 1 ] - p2[ 1 ] );
        for ( const [ px, py, pr ] of puffs ) {
          const up = Math.max( 0, Math.min( 1, ( by - py ) / ( H * 0.06 ) ) );
          const tone = this.mix( grey, white, 0.35 + 0.65 * up );
          for ( const dx2 of [ -W, 0, W ] ) {
            const pg = g.createRadialGradient( px + dx2, py - pr * 0.25, 0, px + dx2, py, pr );
            pg.addColorStop( 0, this.rgba( this.mix( tone, white, 0.35 ), 0.95 ) );
            pg.addColorStop( 0.7, this.rgba( tone, 0.7 ) );
            pg.addColorStop( 1, this.rgba( tone, 0 ) );
            g.fillStyle = pg;
            g.fillRect( px + dx2 - pr, py - pr, pr * 2, pr * 2 );
          }
        }
      }
    }
    // the tile: the sky across two screens, the clouds once on each, cut at the sky's foot
    {
      const th = Math.ceil( hz + 2 ), tile = document.createElement( 'canvas' );
      tile.width = Math.ceil( W * 2 * scale );
      tile.height = Math.ceil( th * scale );
      const tg = tile.getContext( '2d' )!, sy = th * scale;
      for ( const dx of [ 0, W * scale ] ) {
        tg.drawImage( sk.c, 0, 0, sk.c.width, Math.min( sk.c.height, sy ), dx, 0, W * scale, Math.min( sk.c.height, sy ) );
        tg.drawImage( cl.c, 0, 0, cl.c.width, Math.min( cl.c.height, sy ), dx, 0, W * scale, Math.min( cl.c.height, sy ) );
      }
      this.skyTile = tile;
      this.skyH = th;
    }

    // ---- the land ----
    const bk = canvas();
    g = bk.g;
    // the sea to the horizon, silver grey, a little brighter far off
    let landTop = hz - 1;
    {
      const seaTop = hz, [ , seaBot ] = P( 0, 0, 2600 );
      const sea = g.createLinearGradient( 0, seaTop, 0, seaBot );
      sea.addColorStop( 0, this.rgba( [ 214, 224, 228 ], 1 ) );
      sea.addColorStop( 1, this.rgba( [ 176, 194, 200 ], 1 ) );
      g.fillStyle = sea;
      // (owner: white showed at the far left) the water runs on under the far woods down to the lawns, so the
      // estuary's gaps between the woods show water, never the page behind
      const [ , lawnTop ] = P( 0, 0, 430 );
      g.fillRect( 0, seaTop - 1, W, Math.max( seaBot, lawnTop + 2 ) - seaTop + 2 );
      // the island on the horizon at the right, a low hump of grey green
      const isl: [ number, number ][] = [];
      const ix = 0.32 * H * 2600 / F, iz = 6000;
      for ( let q = 0; q <= 30; q++ ) { const u = q / 30, x = ix - 700 + 1400 * u; isl.push( P( x, 120 * Math.pow( Math.sin( u * Math.PI ), 0.8 ) * ( 1 + 0.3 * Math.sin( u * 9 ) ), iz ) ); }
      poly( isl.concat( [ P( ix + 700, 0, iz ), P( ix - 700, 0, iz ) ] ), this.rgba( inAir( [ 100, 120, 110 ], iz, 0.75 ), 1 ) );
      for ( const [ , y ] of isl ) { landTop = Math.min( landTop, y ); }
      // the far shore at the left: low land across the estuary
      const shore: [ number, number ][] = [];
      const sx1 = -0.1 * H * 4500 / F;
      for ( let q = 0; q <= 40; q++ ) { const u = q / 40, x = -W * 4500 / F + ( sx1 + W * 4500 / F ) * u; shore.push( P( x, 30 + 20 * Math.sin( u * 7 ) - 25 * u * u, 4500 ) ); }
      poly( shore.concat( [ P( sx1, 0, 4500 ), P( -W * 4500 / F, 0, 4500 ) ] ), this.rgba( inAir( [ 96, 112, 100 ], 4500, 0.7 ), 1 ) );
      for ( const [ , y ] of shore ) { landTop = Math.min( landTop, y ); }
    }
    // the woods from the coast down toward the park: rows of crowns far to near, the far ones dabs
    // (owner: the trees and everything lighter)
    const greens: RGB[] = [ this.mix( shamrock, mint, 0.2 ), this.mix( shamrock, green, 0.5 ), this.mix( green, mint, 0.15 ), this.mix( green, [ 150, 200, 90 ], 0.3 ), this.mix( this.mix( shamrock, [ 40, 90, 60 ], 0.2 ), mint, 0.15 ) ];
    const autumn: RGB[] = [ orange, this.mix( orange, gold, 0.5 ), this.mix( orange, [ 140, 60, 20 ], 0.3 ) ];
    // (owner: the trees a tone lighter again)
    const crownCol = ( r: () => number ) => this.mix( r() < 0.12 ? autumn[ Math.floor( r() * autumn.length ) ] : greens[ Math.floor( r() * greens.length ) ], mint, 0.18 );
    // a soft blob crown for the far woods: lit at its upper left, shaded low right
    // (owner: nicer trees) each crown a heap of three or four rounded lumps, each lit at its upper left
    const blob = ( x: number, y: number, rx: number, ry: number, col: RGB, z: number ) => {
      const c0 = inAir( col, z ), hi = this.mix( c0, [ 250, 255, 230 ], 0.28 ), lo = this.mix( c0, [ 20, 40, 30 ], 0.22 );
      const lumps: [ number, number, number ][] = [ [ x, y + ry * 0.15, 0.85 ], [ x - rx * 0.42, y + ry * 0.2, 0.62 ], [ x + rx * 0.4, y + ry * 0.12, 0.66 ], [ x + rx * 0.05, y - ry * 0.35, 0.6 ] ];
      for ( const [ lx, ly, k ] of lumps ) {
        const lr = Math.max( rx, ry ) * k;
        const bg = g.createRadialGradient( lx - lr * 0.35, ly - lr * 0.4, 0, lx, ly, lr * 1.1 );
        bg.addColorStop( 0, this.rgba( hi, 1 ) );
        bg.addColorStop( 0.55, this.rgba( c0, 1 ) );
        bg.addColorStop( 1, this.rgba( lo, 1 ) );
        g.fillStyle = bg;
        g.beginPath(); g.ellipse( lx, ly, lr * rx / Math.max( rx, ry ), lr * ry / Math.max( rx, ry ), 0, 0, Math.PI * 2 ); g.fill();
      }
    };
    // the leafy crown for the trees near enough to show their masses (Madrid's plane trees' way)
    // (owner: Dublin stuttered) the leaves of a mass gathered by tone into a few paths and filled at once, not
    // drawn one by one (a third of a million small draws made the scene take most of a second to paint):
    // each leaf the same small lens at the same place, turn and size, its tone in thirteen steps
    const turns = [ 0, 1, 2 ].map( t => [ Math.cos( -0.6 + t * 0.9 ), Math.sin( -0.6 + t * 0.9 ) ] );
    const lens = ( p: Path2D, x: number, y: number, sz: number, turn: number ) => {
      const [ c, sn ] = turns[ turn ], ax = 0.46 * sz * c, ay = 0.46 * sz * sn, bx = -0.4 * sz * -sn, by = -0.4 * sz * c;
      p.moveTo( x - ax, y - ay );
      p.quadraticCurveTo( x + bx, y + by, x + ax, y + ay );
      p.quadraticCurveTo( x - bx, y - by, x - ax, y - ay );
      p.closePath();
    };
    const tree = ( x: number, z: number, hgt: number, wide: number, col: RGB, r: () => number ) => {
      // (owner: the woods, not lollipops) the crowns reach down near the ground, only a stub of trunk below
      const s = F / z, [ bx, by ] = P( x, 0, z ), [ cx, cy ] = P( x, hgt * 0.54, z );
      const rx = wide * s, ry = Math.max( 1, cy - P( x, hgt, z )[ 1 ] );
      // (owner: lighter on the machine) the smaller crowns at the back as simple lumpy heaps
      if ( rx < 11 ) { blob( cx, cy, rx, ry * 1.05, col, z ); return; }
      const b2 = inAir( col, z ), lo = this.mix( b2, [ 16, 36, 26 ], 0.5 ), hi = this.mix( b2, [ 236, 245, 190 ], 0.4 );
      // its shadow on the lawn, cast to the lower right, soft
      const [ shx, shy ] = P( x + hgt * 0.5, 0.1, z + hgt * 0.15 );
      const shg = g.createRadialGradient( shx, shy, 0, shx, shy, rx * 1.3 );
      shg.addColorStop( 0, 'rgba(30,60,30,0.22)' );
      shg.addColorStop( 1, 'rgba(30,60,30,0)' );
      g.fillStyle = shg;
      g.beginPath(); g.ellipse( shx, shy, rx * 1.3, rx * 0.35, 0, 0, Math.PI * 2 ); g.fill();
      // the trunk under the crown
      const tw = Math.max( 0.8, s * 0.35 );
      g.fillStyle = this.rgba( inAir( [ 92, 76, 60 ], z ), 1 );
      g.fillRect( bx - tw, cy, tw * 2, by - cy );
      // the masses: one in the middle, a ring, a few on top, each with its own light and the crown's
      const R0 = Math.min( rx, ry * 1.1 );
      const masses: [ number, number, number, number ][] = [ [ cx, cy, R0 * 0.55, 0.5 ] ];
      for ( let q = 0; q < 10; q++ ) {
        const a = ( q + r() * 0.6 ) / 10 * Math.PI * 2, d = 0.5 + r() * 0.2, up = Math.sin( a ) < 0 ? 1.05 : 0.8;
        masses.push( [ cx + Math.cos( a ) * d * rx * 0.92, cy + Math.sin( a ) * d * ry * up, R0 * ( 0.34 + r() * 0.14 ), r() ] );
      }
      for ( let q = 0; q < 3; q++ ) { masses.push( [ cx + ( r() - 0.5 ) * rx * 0.9, cy - ry * ( 0.45 + r() * 0.25 ), R0 * ( 0.3 + r() * 0.12 ), 0.4 + r() * 0.6 ] ); }
      masses.sort( ( a, b ) => a[ 3 ] - b[ 3 ] );
      const ls = Math.max( 2, s * 0.55 );
      for ( const [ qx, qy, qr, depth ] of masses ) {
        const low = Math.max( 0, Math.min( 1, ( qy - cy ) / ry ) );
        const crownLit = Math.max( -1, Math.min( 1, -( qx - cx ) / rx * 0.7 - ( qy - cy ) / ry * 0.7 ) );
        const dim = Math.max( 0, Math.min( 1, ( 1 - depth ) * 0.22 + low * 0.25 - crownLit * 0.25 + 0.12 ) );
        const mHi = this.mix( hi, b2, dim * 0.7 ), mMid = this.mix( b2, lo, dim * 0.8 ), mLo = this.mix( mMid, lo, 0.55 );
        const mg = g.createRadialGradient( qx - qr * 0.38, qy - qr * 0.42, qr * 0.05, qx, qy, qr * 1.15 );
        mg.addColorStop( 0, this.rgba( mHi, 1 ) );
        mg.addColorStop( 0.6, this.rgba( mMid, 1 ) );
        mg.addColorStop( 1, this.rgba( mLo, 1 ) );
        g.fillStyle = mg;
        g.beginPath();
        for ( let w = 0; w <= 16; w++ ) { const a = w / 16 * Math.PI * 2, kk = qr * ( 0.88 + 0.12 * Math.sin( a * 5 + qx ) ); if ( w ) { g.lineTo( qx + Math.cos( a ) * kk, qy + Math.sin( a ) * kk ); } else { g.moveTo( qx + kk, qy ); } }
        g.closePath(); g.fill();
        const n = Math.min( 120, Math.floor( qr * qr * 0.2 ) + 6 ), steps = 6;
        // the rim's leaves first, then the inner ones over them, as they were drawn one by one
        for ( const phase of [ 0, 1 ] ) {
          const groups = new Map<number, Path2D>();
          for ( let q = phase ? Math.ceil( n * 0.3 ) : 0; q < ( phase ? n : Math.ceil( n * 0.3 ) ); q++ ) {
            const a = r() * Math.PI * 2, rim = q < n * 0.3, d = rim ? qr * ( 0.86 + r() * 0.2 ) : Math.sqrt( r() ) * qr * 0.88;
            const lx = qx + Math.cos( a ) * d, ly = qy + Math.sin( a ) * d, lit = ( -Math.cos( a ) * 0.6 - Math.sin( a ) * 0.8 ) * ( d / qr );
            if ( rim && lit < 0 ) { continue; }
            const kk = lit > 0 ? Math.min( 1, lit * 1.2 + r() * 0.2 ) : -Math.min( 1, -lit * 1.1 + r() * 0.15 );
            const sz = ls * ( 0.7 + r() * 0.6 ), turn = Math.floor( r() * 3 ), key = Math.round( kk * steps );
            let p = groups.get( key );
            if ( !p ) { p = new Path2D(); groups.set( key, p ); }
            lens( p, lx, ly, sz, turn );
          }
          for ( const [ key, p ] of groups ) {
            g.fillStyle = this.rgba( key >= 0 ? this.mix( mMid, mHi, key / steps ) : this.mix( mMid, mLo, -key / steps ), 1 );
            g.fill( p );
          }
        }
      }
    };
    // a cedar: the dark spreading tiers of the photograph's foreground conifers
    const cedar = ( x: number, z: number, hgt: number, wide: number, r: () => number ) => {
      const s = F / z, [ bx, by ] = P( x, 0, z );
      const col = inAir( this.mix( shamrock, [ 20, 60, 50 ], 0.55 ), z );
      g.fillStyle = this.rgba( inAir( [ 80, 66, 54 ], z ), 1 );
      g.fillRect( bx - s * 0.5, P( x, hgt * 0.5, z )[ 1 ], s, by - P( x, hgt * 0.5, z )[ 1 ] );
      const tiers = 6;
      for ( let q = 0; q < tiers; q++ ) {
        const u = q / ( tiers - 1 ), y = hgt * ( 0.25 + 0.72 * u ), w = wide * ( 1 - u * 0.75 ) * ( 0.85 + r() * 0.3 );
        const [ tx, ty ] = P( x + ( r() - 0.5 ) * wide * 0.15, y, z );
        const tw2 = w * s, th = Math.max( 2, hgt * 0.14 * s );
        const tg = g.createLinearGradient( tx - tw2, ty - th, tx + tw2, ty + th );
        tg.addColorStop( 0, this.rgba( this.mix( col, [ 200, 230, 180 ], 0.25 ), 1 ) );
        tg.addColorStop( 0.5, this.rgba( col, 1 ) );
        tg.addColorStop( 1, this.rgba( this.mix( col, [ 10, 25, 20 ], 0.45 ), 1 ) );
        g.fillStyle = tg;
        g.beginPath();
        g.moveTo( tx - tw2, ty + th * 0.4 );
        for ( let w2 = 0; w2 <= 12; w2++ ) { const a = w2 / 12, px = tx - tw2 + tw2 * 2 * a; g.lineTo( px, ty - th * ( 0.6 + 0.4 * Math.sin( a * Math.PI ) ) - Math.sin( a * 23 + q ) * th * 0.15 ); }
        g.lineTo( tx + tw2, ty + th * 0.4 );
        g.quadraticCurveTo( tx, ty + th * 0.9, tx - tw2, ty + th * 0.4 );
        g.fill();
      }
    };

    // the far woods: bands of crowns from the coast to the park's edge, far to near
    {
      const r = rr( 5 );
      for ( let z = 2400; z > 420; z *= 0.93 ) {
        const s = F / z, step = Math.max( 5, 6.5 * z / 600 );
        for ( let x = -W * z / F * 0.6 - 30; x < W * z / F * 0.6 + 30; x += step * ( 0.7 + r() * 0.6 ) ) {
          // the estuary's water shows at the far left between the woods and the shore
          if ( z > 1500 && x < -0.15 * H * z / F && r() < 0.8 ) { continue; }
          const hgt = 14 + r() * 10, wide = 4.5 + r() * 4.5;
          const [ cx, cy ] = P( x, hgt * 0.7, z );
          blob( cx, cy, wide * s, hgt * 0.32 * s, crownCol( r ), z );
        }
      }
      // the roofs of the old buildings in the woods at the right, slate grey, as in the photograph
      for ( const [ x, z, w ] of [ [ 120, 520, 60 ], [ 180, 560, 40 ] ] ) {
        poly( [ P( x - w / 2, 8, z ), P( x + w / 2, 8, z ), P( x + w / 2 - 4, 12, z + 8 ), P( x - w / 2 + 4, 12, z + 8 ) ], this.rgba( inAir( [ 150, 156, 166 ], z ), 1 ) );
      }
    }
    // the lawns: the park's mown grass from the woods' foot to our feet, its mowing stripes running away
    {
      const zFar = 430, zNear = 40;
      const halfW = ( z: number ) => W * z / F * 0.6 + 40;
      const [ , y0 ] = P( 0, 0, zFar );
      const lg2 = g.createLinearGradient( 0, y0, 0, H );
      lg2.addColorStop( 0, this.rgba( inAir( this.mix( this.mix( green, [ 150, 200, 90 ], 0.3 ), mint, 0.15 ), zFar ), 1 ) );
      lg2.addColorStop( 1, this.rgba( this.mix( green, [ 110, 190, 70 ], 0.3 ), 1 ) );
      g.fillStyle = lg2;
      g.fillRect( 0, y0, W, H - y0 );
      // the stripes: bands of the mower going away from us, light and dark by turns
      // (kaizen) broad and faint, as the photograph's lawns
      for ( let x = -400; x < 400; x += 16 ) {
        const band = Math.floor( ( x + 400 ) / 16 ) % 2;
        if ( band ) { continue; }
        poly( [ P( x, 0, zNear ), P( x + 16, 0, zNear ), P( x + 16, 0, zFar ), P( x, 0, zFar ) ], 'rgba(255,255,230,0.035)' );
      }
      void halfW;
    }
    // (owner) the castle's garden: a square of finer, lighter lawn before its front, mown in bands
    {
      const gx0 = CASTLE.x - 20, gx1 = CASTLE.x + 48, gz0 = GARDEN_Z, gz1 = CASTLE.z;
      poly( [ P( gx0, 0.02, gz0 ), P( gx1, 0.02, gz0 ), P( gx1, 0.02, gz1 ), P( gx0, 0.02, gz1 ) ], this.rgba( this.mix( this.mix( green, mint, 0.3 ), [ 160, 210, 100 ], 0.2 ), 1 ) );
      for ( let z = gz0 + 6; z < gz1; z += 12 ) { poly( [ P( gx0, 0.03, z ), P( gx1, 0.03, z ), P( gx1, 0.03, z + 6 ), P( gx0, 0.03, z + 6 ) ], 'rgba(255,255,230,0.06)' ); }
    }
    // the near woods round the park, sorted far to near with the castle among them
    const items: { x: number, z: number, hgt: number, wide: number, draw: () => void }[] = [];
    {
      const r = rr( 17 );
      // behind the castle and along the lawns' far edge
      // (owner: the woods start on one line everywhere, the castle back at their edge)
      for ( let z = 420; z > WOODS_EDGE; z -= 9 + r() * 7 ) {
        for ( let x = -W * z / F * 0.62 - 20; x < W * z / F * 0.62 + 20; x += 6 + r() * 6 ) {
          // the lawns open in the middle and right in front of the far woods
          // (owner: the left side as the right) the lawns open alike to both sides, the woods' edge at one
          // depth each side; a grove stands behind the castle, the castle's own clearing stays open
          // (owner: a square garden before the castle, no trees on it) the castle's lawn open to the park
          // (owner: the woods in a gentle arc) their edge farther off in the middle, curving toward us at the sides
          // (owner: the same arc on a wide screen) its span the visible half width at the edge's depth
          const span = 0.53 * W * 220 / F, u = Math.min( 1, ( x / span ) * ( x / span ) );
          if ( z < WOODS_EDGE + 70 * ( 1 - u ) ) { continue; }
          // (owner: the woods begin again in front of the garden) the garden a clearing in the woods
          const inFront = x > CASTLE.x - 20 && x < CASTLE.x + 48;
          if ( inFront && z > GARDEN_Z && z < CASTLE.z + 35 ) { continue; }
          // (owner: the trees before the castle as tall as the rest)
          const front = inFront && z <= GARDEN_Z;
          const xx = x, zz = Math.min( z + r() * 10, front ? GARDEN_Z : 1e9 ), hgt = 16 + r() * 12, wide = 5.5 + r() * 4.5, col = crownCol( r ), seed = Math.floor( r() * 1e6 );
          items.push( { x: xx, z: zz, hgt, wide, draw: () => tree( xx, zz, hgt, wide, col, rr( seed ) ) } );
        }
      }
      // (owner: the trees in front were poor) none stand alone on the lawns; the woods behind stay
    }
    // (owner: no path) the drive removed
    // the castle: grey stone walls under slate, two round towers crenellated at the front corners, a taller
    // square tower behind, sash windows, ivy on its walls; lit from the upper left
    // (owner: the trees swaying in the wind) the woods behind the castle stay on the back layer, the castle on
    // its own, the woods before it on a front layer, so the two layers of trees can sway and the castle stand
    items.sort( ( a, b ) => b.z - a.z );
    for ( const it of items ) { if ( it.z >= CASTLE.z ) { it.draw(); } }
    const cs = canvas();
    g = cs.g;
    this.castle( g, P, F, sunK, inAir, shamrock );
    this.castleLayer = cs.c;
    const fw = canvas();
    g = fw.g;
    for ( const it of items ) { if ( it.z < CASTLE.z ) { it.draw(); } }
    this.frontWoods = fw.c;
    g = bk.g;
    this.swayTop = 0;
    this.swayBottom = this.P( 0, 0, WOODS_EDGE * 0.9 )[ 1 ];
    // the rows each layer holds: the land from the island's top, its woods down to the foot of the trees
    // behind the castle; the woods before the castle from their crowns to their shadows; the castle's box
    {
      const foot = ( it: { x: number, z: number, wide: number } ) => P( it.x, 0, it.z )[ 1 ] + it.wide * F / it.z * 0.6 + 6;
      const crown = ( it: { x: number, z: number, hgt: number } ) => P( it.x, it.hgt * 1.3, it.z )[ 1 ] - 6;
      let backFoot = P( 0, 0, 420 )[ 1 ], fTop = H, fBot = 0;
      for ( const it of items ) {
        if ( it.z >= CASTLE.z ) { backFoot = Math.max( backFoot, foot( it ) ); } else { fTop = Math.min( fTop, crown( it ) ); fBot = Math.max( fBot, foot( it ) ); }
      }
      const clampY = ( y: number ) => Math.max( 0, Math.min( H, y ) );
      this.backRows = [ clampY( Math.floor( landTop - 3 ) ), clampY( Math.ceil( backFoot ) ), H ];
      this.frontRows = fBot > fTop ? [ clampY( Math.floor( fTop ) ), clampY( Math.ceil( fBot ) ) ] : [ 0, 0 ];
      let x0 = W, y0 = H, x1 = 0, y1 = 0;
      for ( const cx of [ -11, 28 ] ) { for ( const cy of [ 0, 26 ] ) { for ( const cz of [ -7, 19 ] ) {
        const [ px, py ] = P( CASTLE.x + cx * CASTLE_KS, cy * CASTLE_KS, CASTLE.z + cz * CASTLE_KS );
        x0 = Math.min( x0, px ); x1 = Math.max( x1, px ); y0 = Math.min( y0, py ); y1 = Math.max( y1, py );
      } } }
      x0 = Math.max( 0, Math.floor( x0 - 8 ) ); y0 = Math.max( 0, Math.floor( y0 - 8 ) );
      x1 = Math.min( W, Math.ceil( x1 + 8 ) ); y1 = Math.min( H, Math.ceil( y1 + 8 ) );
      this.castleRect = [ x0, y0, Math.max( 0, x1 - x0 ), Math.max( 0, y1 - y0 ) ];
    }
    void mint;
    this.back = bk.c;
  }

  /**
   * (owner: the castle in detail, after the photograph) Malahide Castle seen from in front: the big round
   * tower at the left with its corbelled crenels, the square block beside it with a corner turret, the
   * range in the middle with its pointed Gothic windows in white tracery under the slate roof and its
   * dormers, the tall square keep behind, chimneys, the slender round tower at the right; ashlar coursing
   * on the grey stone, ivy climbing the lower walls; lit from the upper left.
   */
  private castle( g: CanvasRenderingContext2D, P0: ( x: number, y: number, z: number ) => [ number, number ], F: number,
                  sunK: ( nx: number, nz: number ) => number, inAir: ( col: RGB, z: number, k?: number ) => RGB, ivyBase: RGB ): void {
    const KS = CASTLE_KS;
    const P = ( x: number, y: number, z: number ) => P0( CASTLE.x + ( x - CASTLE.x ) * KS, y * KS, CASTLE.z + ( z - CASTLE.z ) * KS );
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    const r = this.seededLocal( 23 );
    const stone: RGB = [ 190, 180, 160 ], shade: RGB = [ 132, 126, 116 ], slate: RGB = [ 118, 126, 138 ];
    const face = ( nx: number, nz: number, z: number, base = stone ) => inAir( this.mix( this.mix( base, shade, 0.5 ), this.mix( base, [ 252, 250, 240 ], 0.35 ), sunK( nx, nz ) ), z );
    const { x: X, z: Z } = CASTLE;
    const s = F / Z;
    const rgb = ( c: RGB ) => this.rgba( c, 1 );
    // the stone's coursing and a few weathered stones, faint, on a front face from x0 to x1
    const ashlar = ( x0: number, x1: number, y0: number, y1: number, z: number, col: RGB ) => {
      g.strokeStyle = this.rgba( this.mix( col, [ 80, 76, 70 ], 0.35 ), 0.35 );
      g.lineWidth = Math.max( 0.4, s * 0.03 );
      for ( let y = y0 + 0.6; y < y1; y += 0.6 ) { const [ ax, ay ] = P( x0, y, z - 0.01 ), [ bx ] = P( x1, y, z - 0.01 ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, ay ); g.stroke(); }
      for ( let q = 0; q < ( x1 - x0 ) * ( y1 - y0 ) * 0.25; q++ ) {
        const x = x0 + r() * ( x1 - x0 ), y = y0 + r() * ( y1 - y0 );
        poly( [ P( x, y, z - 0.01 ), P( x + 0.9, y, z - 0.01 ), P( x + 0.9, y + 0.55, z - 0.01 ), P( x, y + 0.55, z - 0.01 ) ], this.rgba( this.mix( col, r() < 0.5 ? [ 120, 112, 100 ] : [ 240, 236, 222 ], 0.18 ), 1 ) );
      }
    };
    // a pointed Gothic window: its stone dressing, the dark glass, the white tracery (two lights and a quatrefoil)
    const gothic = ( x: number, y: number, w: number, h: number, z: number ) => {
      const arch = ( x0: number, x1: number, yb: number, ht: number, d: number ) => {
        const pts: [ number, number ][] = [ P( x0, yb, z - d ) ];
        const yt = yb + ht, sp = yt - ( x1 - x0 ) * 0.85;
        pts.push( P( x0, sp, z - d ) );
        for ( let q = 1; q <= 8; q++ ) { const u = q / 8; pts.push( P( x0 + ( x1 - x0 ) * 0.5 * u, sp + ( yt - sp ) * Math.sin( u * Math.PI / 2 ), z - d ) ); }
        for ( let q = 7; q >= 0; q-- ) { const u = q / 8; pts.push( P( x1 - ( x1 - x0 ) * 0.5 * u, sp + ( yt - sp ) * Math.sin( u * Math.PI / 2 ), z - d ) ); }
        pts.push( P( x1, yb, z - d ) );
        return pts;
      };
      poly( arch( x - 0.18, x + w + 0.18, y - 0.2, h + 0.35, 0.02 ), rgb( inAir( [ 222, 216, 200 ], z ) ) );
      poly( arch( x, x + w, y, h, 0.03 ), rgb( inAir( [ 62, 72, 84 ], z ) ) );
      if ( s * w > 5 ) {
        g.strokeStyle = this.rgba( inAir( [ 236, 234, 226 ], z ), 1 );
        g.lineWidth = Math.max( 0.5, s * 0.07 );
        const [ mx0, my0 ] = P( x + w / 2, y, z - 0.04 ), [ , my1 ] = P( x + w / 2, y + h * 0.62, z - 0.04 );
        g.beginPath(); g.moveTo( mx0, my0 ); g.lineTo( mx0, my1 ); g.stroke();
        const [ tx, ty ] = P( x, y + h * 0.42, z - 0.04 ), [ tx1 ] = P( x + w, y + h * 0.42, z - 0.04 );
        g.beginPath(); g.moveTo( tx, ty ); g.lineTo( tx1, ty ); g.stroke();
        const [ qx, qy ] = P( x + w / 2, y + h * 0.78, z - 0.04 );
        g.beginPath(); g.arc( qx, qy, s * w * 0.16, 0, Math.PI * 2 ); g.stroke();
      }
    };
    // (owner: the battlements in 3D, their sides and backs) a flat roof's walk seen from above, drawn far to
    // near: the lead roof inside; the back parapet's inner face with its merlons and their copings; the left
    // parapet's inner face (the camera stands to the castle's right); the right parapet's outer face over the
    // end wall; last the front parapet standing out on its corbels, each merlon with its top and its side
    const parapet = ( x0: number, x1: number, z0: number, z1: number, h: number, col: RGB ) => {
      const t = 0.5, hp = h + 0.7, hm = h + 1.4, sp = 1.1, mw = 0.6, zf = z0 - 0.3;
      const inner = this.mix( col, shade, 0.35 ), side = face( 1, 0, z0 ), top = this.mix( col, [ 252, 250, 240 ], 0.25 );
      const lead = inAir( this.mix( slate, [ 88, 90, 96 ], 0.45 ), z0 );
      const q4 = ( a: [ number, number ][], c: RGB ) => poly( a, rgb( c ) );
      q4( [ P( x0 + t, h, z0 + t ), P( x1 - t, h, z0 + t ), P( x1 - t, h, z1 - t ), P( x0 + t, h, z1 - t ) ], lead );
      // the back parapet
      q4( [ P( x0 + t, h, z1 - t ), P( x1 - t, h, z1 - t ), P( x1 - t, hp, z1 - t ), P( x0 + t, hp, z1 - t ) ], inner );
      q4( [ P( x0, hp, z1 - t ), P( x1, hp, z1 - t ), P( x1, hp, z1 ), P( x0, hp, z1 ) ], top );
      for ( let x = x0; x < x1 - 0.3; x += sp ) {
        const xb = Math.min( x1, x + mw );
        q4( [ P( x, hp, z1 - t ), P( xb, hp, z1 - t ), P( xb, hm, z1 - t ), P( x, hm, z1 - t ) ], inner );
        q4( [ P( x, hm, z1 - t ), P( xb, hm, z1 - t ), P( xb, hm, z1 ), P( x, hm, z1 ) ], top );
      }
      // the left parapet, its inner face toward us
      const innerL = this.mix( face( 1, 0, z0 ), shade, 0.25 );
      q4( [ P( x0 + t, h, z0 + t ), P( x0 + t, h, z1 - t ), P( x0 + t, hp, z1 - t ), P( x0 + t, hp, z0 + t ) ], innerL );
      q4( [ P( x0, hp, z0 ), P( x0 + t, hp, z0 ), P( x0 + t, hp, z1 ), P( x0, hp, z1 ) ], top );
      for ( let z = z1 - 0.3 - mw; z > z0; z -= sp ) {
        const za = Math.max( z0 + t, z );
        q4( [ P( x0 + t, hp, za ), P( x0 + t, hp, z + mw ), P( x0 + t, hm, z + mw ), P( x0 + t, hm, za ) ], innerL );
        q4( [ P( x0, hm, za ), P( x0 + t, hm, za ), P( x0 + t, hm, z + mw ), P( x0, hm, z + mw ) ], top );
      }
      // the right parapet: its outer face over the end wall, its merlons far to near
      q4( [ P( x1, h, zf ), P( x1, h, z1 ), P( x1, hp, z1 ), P( x1, hp, zf ) ], side );
      q4( [ P( x1 - t, hp, zf ), P( x1, hp, zf ), P( x1, hp, z1 ), P( x1 - t, hp, z1 ) ], top );
      for ( let z = z1 - 0.3 - mw; z > zf - 0.05; z -= sp ) {
        const za = Math.max( zf, z );
        q4( [ P( x1, hp, za ), P( x1, hp, z + mw ), P( x1, hm, z + mw ), P( x1, hm, za ) ], side );
        q4( [ P( x1 - t, hm, za ), P( x1, hm, za ), P( x1, hm, z + mw ), P( x1 - t, hm, z + mw ) ], top );
      }
      // the front parapet on its corbels: the shadow under it, its face, its coping, the merlons
      q4( [ P( x0, h - 0.35, z0 - 0.05 ), P( x1, h - 0.35, z0 - 0.05 ), P( x1, h, zf ), P( x0, h, zf ) ], this.mix( col, [ 50, 48, 46 ], 0.3 ) );
      const lit = this.mix( col, [ 252, 250, 240 ], 0.1 );
      q4( [ P( x0, h, zf ), P( x1, h, zf ), P( x1, hp, zf ), P( x0, hp, zf ) ], lit );
      q4( [ P( x0, hp, zf ), P( x1, hp, zf ), P( x1, hp, zf + t ), P( x0, hp, zf + t ) ], top );
      for ( let x = x0; x < x1 - 0.3; x += sp ) {
        const xb = Math.min( x1, x + mw );
        q4( [ P( xb, hp, zf ), P( xb, hp, zf + t ), P( xb, hm, zf + t ), P( xb, hm, zf ) ], side );
        q4( [ P( x, hp, zf ), P( xb, hp, zf ), P( xb, hm, zf ), P( x, hm, zf ) ], lit );
        q4( [ P( x, hm, zf ), P( xb, hm, zf ), P( xb, hm, zf + t ), P( x, hm, zf + t ) ], top );
      }
    };
    // a block: front face, the end face toward us, a roof (pitched or flat with battlements)
    const block = ( x0: number, x1: number, z0: number, z1: number, h: number, roof: number, battle: boolean ) => {
      const front = face( 0, -1, z0 );
      poly( [ P( x0, 0, z0 ), P( x1, 0, z0 ), P( x1, h, z0 ), P( x0, h, z0 ) ], rgb( front ) );
      const ex = ( x0 + x1 ) / 2 < 0 ? x1 : x0, en = ( x0 + x1 ) / 2 < 0 ? 1 : -1;
      poly( [ P( ex, 0, z0 ), P( ex, 0, z1 ), P( ex, h, z1 ), P( ex, h, z0 ) ], rgb( face( en, 0, z0 ) ) );
      ashlar( x0, x1, 0.4, h - 0.4, z0, front );
      if ( roof > 0 ) {
        const zm = ( z0 + z1 ) / 2;
        const rg = g.createLinearGradient( 0, P( 0, h + roof, zm )[ 1 ], 0, P( 0, h, z0 )[ 1 ] );
        rg.addColorStop( 0, this.rgba( inAir( this.mix( slate, [ 220, 228, 236 ], 0.35 ), z0 ), 1 ) );
        rg.addColorStop( 1, this.rgba( inAir( slate, z0 ), 1 ) );
        poly( [ P( x0, h, z0 ), P( x1, h, z0 ), P( x1, h + roof, zm ), P( x0, h + roof, zm ) ], rg );
        poly( [ P( ex, h, z0 ), P( ex, h + roof, zm ), P( ex, h, z1 ) ], rgb( face( en, 0, z0, slate ) ) );
        // its slates in faint courses
        g.strokeStyle = this.rgba( inAir( [ 80, 86, 98 ], z0 ), 0.4 );
        g.lineWidth = Math.max( 0.4, s * 0.025 );
        for ( let u = 0.15; u < 1; u += 0.15 ) { const [ ax, ay ] = P( x0, h + roof * u, z0 + ( zm - z0 ) * u ), [ bx ] = P( x1, h + roof * u, z0 + ( zm - z0 ) * u ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx, ay ); g.stroke(); }
      }
      if ( battle ) { parapet( x0, x1, z0, z1, h, front ); }
    };
    // a round tower: its drum shaded round, the corbelled crenellated top, slits and windows
    const tower = ( cx: number, cz: number, rad: number, h: number ) => {
      const N = 40;
      const pt = ( a: number, y: number, r2 = rad ) => P( cx + Math.sin( a ) * r2, y, cz - Math.cos( a ) * r2 );
      const visible = ( am: number ) => -Math.cos( am ) < 0.15;
      const seg = ( q: number ) => { const a0 = q / N * Math.PI * 2, a1 = ( q + 1 ) / N * Math.PI * 2; return [ a0, a1, ( a0 + a1 ) / 2 ]; };
      // the drum
      for ( let q = 0; q < N; q++ ) {
        const [ a0, a1, am ] = seg( q ), nx = Math.sin( am ), nz = -Math.cos( am );
        if ( !visible( am ) ) { continue; }
        const col = face( nx, nz, cz );
        poly( [ pt( a0, 0 ), pt( a1, 0 ), pt( a1, h ), pt( a0, h ) ], rgb( col ) );
        // coursing on the drum
        for ( let y = 0.6; y < h; y += 0.6 ) { poly( [ pt( a0, y ), pt( a1, y ), pt( a1, y + 0.05 ), pt( a0, y + 0.05 ) ], this.rgba( this.mix( col, [ 80, 76, 70 ], 0.35 ), 0.3 ) ); }
      }
      // (owner: the tops in 3D, their sides and backs) the top seen from above: the lead floor inside the ring,
      // the far half of the parapet's inner face with its merlons, the copings on top, then the near half
      const ro = rad + 0.5, hp = h + 0.9, hm = h + 1.7;
      const topC = ( col: RGB ) => this.mix( col, [ 252, 250, 240 ], 0.25 );
      const floor: [ number, number ][] = [];
      for ( let q = 0; q < N; q++ ) { floor.push( pt( q / N * Math.PI * 2, h, rad ) ); }
      poly( floor, rgb( inAir( this.mix( slate, [ 88, 90, 96 ], 0.45 ), cz ) ) );
      const far: number[] = [];
      for ( let q = 0; q < N; q++ ) { if ( Math.cos( seg( q )[ 2 ] ) < 0.2 ) { far.push( q ); } }
      // the far half from the back out to its sides, so the nearer pieces lie over the farther
      far.sort( ( a, b ) => Math.cos( seg( a )[ 2 ] ) - Math.cos( seg( b )[ 2 ] ) );
      for ( const q of far ) {
        const [ a0, a1, am ] = seg( q ), inner = this.mix( face( -Math.sin( am ), Math.cos( am ), cz ), shade, 0.3 );
        poly( [ pt( a0, h, rad ), pt( a1, h, rad ), pt( a1, hp, rad ), pt( a0, hp, rad ) ], rgb( inner ) );
        const yt = q % 2 === 0 ? hm : hp;
        if ( q % 2 === 0 ) { poly( [ pt( a0, hp, rad ), pt( a1, hp, rad ), pt( a1, hm, rad ), pt( a0, hm, rad ) ], rgb( inner ) ); }
        poly( [ pt( a0, yt, rad ), pt( a1, yt, rad ), pt( a1, yt, ro ), pt( a0, yt, ro ) ], rgb( topC( face( Math.sin( am ), -Math.cos( am ), cz ) ) ) );
      }
      // the near half: the corbels, the parapet standing out and its crenels, their copings
      for ( let q = 0; q < N; q++ ) {
        const [ a0, a1, am ] = seg( q ), nx = Math.sin( am ), nz = -Math.cos( am );
        if ( !visible( am ) ) { continue; }
        const col = face( nx, nz, cz );
        poly( [ pt( a0, h - 0.6, rad ), pt( a1, h - 0.6, rad ), pt( a1, h, ro ), pt( a0, h, ro ) ], rgb( this.mix( col, [ 40, 40, 40 ], 0.35 ) ) );
        if ( q % 2 === 0 ) { poly( [ pt( am - 0.02, h - 0.9, rad + 0.02 ), pt( am + 0.04, h - 0.9, rad + 0.02 ), pt( am + 0.04, h - 0.1, rad + 0.45 ), pt( am - 0.02, h - 0.1, rad + 0.45 ) ], rgb( this.mix( col, [ 252, 250, 240 ], 0.15 ) ) ); }
        poly( [ pt( a0, h, ro ), pt( a1, h, ro ), pt( a1, hp, ro ), pt( a0, hp, ro ) ], rgb( this.mix( col, [ 252, 250, 240 ], 0.12 ) ) );
        if ( q % 2 === 0 ) {
          poly( [ pt( a0, hp, ro ), pt( a1, hp, ro ), pt( a1, hm, ro ), pt( a0, hm, ro ) ], rgb( this.mix( col, [ 252, 250, 240 ], 0.12 ) ) );
          poly( [ pt( a0, hm, rad ), pt( a1, hm, rad ), pt( a1, hm, ro ), pt( a0, hm, ro ) ], rgb( topC( col ) ) );
        } else {
          poly( [ pt( a0, hp, rad ), pt( a1, hp, rad ), pt( a1, hp, ro ), pt( a0, hp, ro ) ], rgb( topC( col ) ) );
        }
      }
      // the windows and slits on its face toward us
      for ( const [ am, y, w, hh ] of [ [ -0.35, 3.5, 0.25, 1.6 ], [ 0.25, 8, 0.32, 2.2 ], [ -0.2, 12.5, 0.3, 2 ] ] as [ number, number, number, number ][] ) {
        if ( y + hh > h - 1.5 ) { continue; }
        poly( [ pt( am - w / rad, y - 0.15, rad + 0.02 ), pt( am + w / rad, y - 0.15, rad + 0.02 ), pt( am + w / rad, y + hh + 0.15, rad + 0.02 ), pt( am - w / rad, y + hh + 0.15, rad + 0.02 ) ], rgb( inAir( [ 222, 216, 200 ], cz ) ) );
        poly( [ pt( am - w * 0.7 / rad, y, rad + 0.03 ), pt( am + w * 0.7 / rad, y, rad + 0.03 ), pt( am + w * 0.7 / rad, y + hh, rad + 0.03 ), pt( am - w * 0.7 / rad, y + hh, rad + 0.03 ) ], rgb( inAir( [ 62, 72, 84 ], cz ) ) );
      }
    };
    // a chimney stack
    const chimney = ( x: number, z: number, y0: number, h: number ) => {
      poly( [ P( x - 0.6, y0, z ), P( x + 0.6, y0, z ), P( x + 0.6, y0 + h, z ), P( x - 0.6, y0 + h, z ) ], rgb( face( 0, -1, z ) ) );
      poly( [ P( x - 0.75, y0 + h, z - 0.1 ), P( x + 0.75, y0 + h, z - 0.1 ), P( x + 0.75, y0 + h + 0.3, z - 0.1 ), P( x - 0.75, y0 + h + 0.3, z - 0.1 ) ], rgb( this.mix( face( 0, -1, z ), [ 252, 250, 240 ], 0.15 ) ) );
    };
    // ivy in soft dabs over a front from x0 to x1 up to a ragged height
    const ivy = ( x0: number, x1: number, top: number, z: number ) => {
      for ( let q = 0; q < ( x1 - x0 ) * top * 0.9; q++ ) {
        const x = x0 + r() * ( x1 - x0 ), lim = top * ( 0.55 + 0.45 * Math.sin( x * 1.3 ) ** 2 ), y = Math.pow( r(), 0.7 ) * lim;
        const [ px, py ] = P( x, y, z - 0.08 );
        g.fillStyle = this.rgba( inAir( this.mix( ivyBase, r() < 0.5 ? [ 40, 90, 50 ] : [ 150, 200, 110 ], 0.3 + r() * 0.3 ), z ), 0.92 );
        g.beginPath(); g.arc( px, py, s * ( 0.35 + r() * 0.4 ), 0, Math.PI * 2 ); g.fill();
      }
    };

    // far to near: the keep and the roofs behind, the chimneys, the range, the blocks, the towers in front
    block( X + 5, X + 12, Z + 9, Z + 17, 22, 0, true );
    block( X + 13, X + 26, Z + 7, Z + 15, 13, 5, false );
    chimney( X + 16, Z + 9, 14, 4 );
    chimney( X + 22, Z + 9, 14, 3.5 );
    // the range in the middle: two floors of Gothic windows, the slate roof with two dormers
    block( X + 3, X + 21, Z, Z + 9, 11, 4.5, false );
    for ( const x of [ X + 5, X + 9, X + 13, X + 17 ] ) {
      gothic( x, 2.2, 1.5, 3.2, Z );
      gothic( x, 6.7, 1.5, 3, Z );
    }
    for ( const x of [ X + 8, X + 15 ] ) {
      const zr = Z + 1.6;
      poly( [ P( x, 11.8, zr ), P( x + 2, 11.8, zr ), P( x + 2, 13.6, zr ), P( x + 1, 14.4, zr ), P( x, 13.6, zr ) ], rgb( face( 0, -1, zr ) ) );
      poly( [ P( x + 0.45, 12, zr - 0.02 ), P( x + 1.55, 12, zr - 0.02 ), P( x + 1.55, 13.4, zr - 0.02 ), P( x + 0.45, 13.4, zr - 0.02 ) ], rgb( inAir( [ 62, 72, 84 ], zr ) ) );
    }
    // the square block at the left beside the big tower, crenellated, a little corner turret
    block( X - 2, X + 4, Z - 1, Z + 7, 15, 0, true );
    gothic( X - 0.2, 4, 1.4, 2.8, Z - 1 );
    gothic( X - 0.2, 9, 1.4, 2.6, Z - 1 );
    tower( X + 3.6, Z - 1.1, 0.9, 17 );
    // the door in the range under its arch
    {
      const [ dx0, dy0 ] = P( X + 10.6, 2.6, Z - 0.05 ), [ dx1, dy1 ] = P( X + 12.4, 0, Z - 0.05 );
      g.fillStyle = rgb( inAir( [ 70, 54, 44 ], Z ) );
      g.beginPath(); g.moveTo( dx0, dy1 ); g.lineTo( dx0, dy0 + ( dx1 - dx0 ) * 0.5 ); g.arc( ( dx0 + dx1 ) / 2, dy0 + ( dx1 - dx0 ) * 0.5, ( dx1 - dx0 ) / 2, Math.PI, 0 ); g.lineTo( dx1, dy1 ); g.closePath(); g.fill();
    }
    ivy( X - 2, X + 4, 6, Z - 1 );
    ivy( X + 3, X + 9, 4, Z );
    ivy( X + 17, X + 21, 5, Z );
    // the towers at the front corners: the big one at the left, the slender one at the right
    tower( X - 4.5, Z - 0.5, 4.6, 19 );
    tower( X + 23.5, Z - 0.2, 3.2, 18 );
    ivy( X - 8.5, X - 1, 5, Z - 5 );
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The five colours of the theme's circle: Dublin's mint, green, shamrock, orange and gold. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 211, 243, 219 ], [ 108, 198, 130 ], [ 22, 155, 98 ], [ 226, 107, 3 ], [ 250, 202, 115 ] ];
  }
}
